import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatIst, inboxApi } from '@wa/shared';

/**
 * Broadcasts that have not gone yet — the reference's second rail entry.
 *
 * A separate screen rather than a filter on the thread, because the question it
 * answers is cross-list: "what is going out this week", which no single list's
 * history can show. Each row links to its list, where the send will appear once
 * it has happened, and can be cancelled from here, because the moment somebody
 * remembers the price was wrong is rarely while looking at the list.
 */
export function ScheduledBroadcastsPage() {
  const queryClient = useQueryClient();
  const campaigns = useQuery({ queryKey: ['campaigns'], queryFn: inboxApi.campaigns, refetchInterval: 30_000 });
  const segments = useQuery({ queryKey: ['segments'], queryFn: inboxApi.segments });
  const cancel = useMutation({
    mutationFn: inboxApi.cancelCampaign,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });

  const scheduled = (campaigns.data ?? [])
    .filter((c) => c.status === 1)
    .sort((a, b) => (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? ''));
  const listName = (id: number | null | undefined) => (segments.data ?? []).find((s) => s.id === id)?.name ?? 'a list';

  return (
    <div className="flex h-full min-h-0 flex-col bg-(--color-paper)">
      <header className="flex h-14 shrink-0 items-center border-b border-(--color-line) bg-(--color-surface) px-4">
        <h1 className="text-lead font-semibold">Scheduled Broadcasts</h1>
      </header>
      <div className="pane-scroll min-h-0 flex-1 overflow-y-auto p-3 sm:p-6">
        <div className="mx-auto min-h-[60vh] max-w-3xl rounded-(--radius-panel) border border-(--color-line) bg-(--color-surface) shadow-sm">
          {campaigns.isPending && <p className="p-10 text-center text-small text-(--color-muted)">Loading…</p>}
          {!campaigns.isPending && scheduled.length === 0 && (
            <p className="grid min-h-[60vh] place-items-center text-body text-(--color-faint)">No scheduled broadcasts</p>
          )}
          <ul>
            {scheduled.map((c) => (
              <li key={c.id} className="flex items-center gap-3 border-b border-(--color-line-dim) px-4 py-3 last:border-b-0">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-body font-semibold">{c.templateName}</span>
                  <span className="block truncate text-small text-(--color-muted)">
                    to <Link to={`/broadcasts/${c.segmentId ?? ''}`} className="font-medium text-(--color-brand) hover:underline">{listName(c.segmentId)}</Link>
                    {' · '}{c.totalRecipients > 0 ? `${c.totalRecipients.toLocaleString('en-IN')} people · ` : ''}
                    {formatIst(c.scheduledAt)}
                  </span>
                </span>
                <button
                  type="button"
                  disabled={cancel.isPending}
                  onClick={() => cancel.mutate(c.id)}
                  className="tap shrink-0 rounded-(--radius-field) border border-(--color-line) px-3 py-1.5 text-body font-medium text-(--color-bad) hover:bg-(--color-sunken) disabled:opacity-60"
                >
                  Cancel
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
